import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
  FiActivity,
  FiBarChart2,
  FiPieChart,
  FiCalendar,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiArrowUp,
  FiArrowDown
} = FiIcons;

const StatCard = ({ icon, title, value, change, changeType }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
  >
    <div className="flex items-center justify-between mb-4">
      <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg">
        <SafeIcon icon={icon} className="w-6 h-6 text-indigo-600" />
      </div>
      {change && (
        <div className={`flex items-center space-x-1 text-sm font-medium ${
          changeType === 'positive' ? 'text-green-600' : 'text-red-600'
        }`}>
          <SafeIcon icon={changeType === 'positive' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
          <span>{change}</span>
        </div>
      )}
    </div>
    <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
  </motion.div>
);

const AdvancedAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [activeTab, setActiveTab] = useState('overview');

  const stats = {
    totalRevenue: '$124,563',
    totalPatients: '2,847',
    conversionRate: '68.4%',
    avgSessionTime: '12m 34s',
  };

  const revenueData = [
    { month: 'Jan', value: 45000 },
    { month: 'Feb', value: 52000 },
    { month: 'Mar', value: 48000 },
    { month: 'Apr', value: 61000 },
    { month: 'May', value: 55000 },
    { month: 'Jun', value: 67000 },
  ];

  const topReferrers = [
    { name: 'Dr. Sarah Mitchell', referrals: 142, revenue: '$45,230' },
    { name: 'Dr. Michael Chen', referrals: 128, revenue: '$38,940' },
    { name: 'Dr. Lisa Rodriguez', referrals: 115, revenue: '$34,120' },
    { name: 'Dr. James Wilson', referrals: 98, revenue: '$29,450' },
    { name: 'Dr. Emily Johnson', referrals: 87, revenue: '$26,180' },
  ];

  const patientDemographics = [
    { category: '18-30', percentage: 25, count: 712 },
    { category: '31-45', percentage: 35, count: 996 },
    { category: '46-60', percentage: 28, count: 797 },
    { category: '60+', percentage: 12, count: 342 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics</h1>
              <p className="text-gray-600 mt-1">Comprehensive insights into your practice performance</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="90days">Last 90 days</option>
                <option value="1year">Last year</option>
              </select>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Export</span>
              </button>
              <button className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiDollarSign}
            title="Total Revenue"
            value={stats.totalRevenue}
            change="+12.5%"
            changeType="positive"
          />
          <StatCard
            icon={FiUsers}
            title="Total Patients"
            value={stats.totalPatients}
            change="+8.2%"
            changeType="positive"
          />
          <StatCard
            icon={FiTrendingUp}
            title="Conversion Rate"
            value={stats.conversionRate}
            change="-2.4%"
            changeType="negative"
          />
          <StatCard
            icon={FiActivity}
            title="Avg Session Time"
            value={stats.avgSessionTime}
            change="+5.7%"
            changeType="positive"
          />
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
        >
          <div className="flex space-x-8 p-6 border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'referrers', label: 'Top Referrers' },
              { id: 'demographics', label: 'Demographics' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {/* Revenue Chart */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                  <SafeIcon icon={FiBarChart2} className="w-5 h-5 text-gray-400" />
                </div>
                <div className="h-64 flex items-end justify-between space-x-2">
                  {revenueData.map((item, index) => (
                    <div key={item.month} className="flex-1 flex flex-col items-center">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${(item.value / 70000) * 100}%` }}
                        transition={{ delay: index * 0.1 }}
                        className="w-full bg-gradient-to-t from-indigo-600 to-purple-600 rounded-t-lg mb-2"
                      />
                      <span className="text-xs text-gray-600">{item.month}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
                  <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Active Dealrooms</span>
                    <span className="text-xl font-bold text-gray-900">48</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Pending Referrals</span>
                    <span className="text-xl font-bold text-gray-900">23</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Completed Cases</span>
                    <span className="text-xl font-bold text-gray-900">156</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Avg Case Duration</span>
                    <span className="text-xl font-bold text-gray-900">18 days</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'revenue' && (
            <motion.div
              key="revenue"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue Analysis</h3>
              <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                  <SafeIcon icon={FiBarChart2} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Detailed revenue charts coming soon</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'referrers' && (
            <motion.div
              key="referrers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Referring Providers</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-600 border-b border-gray-200">
                        <th className="pb-3 font-medium">Rank</th>
                        <th className="pb-3 font-medium">Provider</th>
                        <th className="pb-3 font-medium">Referrals</th>
                        <th className="pb-3 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topReferrers.map((referrer, index) => (
                        <motion.tr
                          key={referrer.name}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="border-b border-gray-100 last:border-0"
                        >
                          <td className="py-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full font-semibold text-sm">
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-4 font-medium text-gray-900">{referrer.name}</td>
                          <td className="py-4 text-gray-600">{referrer.referrals}</td>
                          <td className="py-4 font-semibold text-gray-900">{referrer.revenue}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'demographics' && (
            <motion.div
              key="demographics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Patient Demographics by Age</h3>
              <div className="space-y-6">
                {patientDemographics.map((demo, index) => (
                  <motion.div
                    key={demo.category}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium">{demo.category} years</span>
                      <span className="text-gray-600">{demo.count} patients ({demo.percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${demo.percentage}%` }}
                        transition={{ delay: index * 0.1, duration: 0.5 }}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;